import React, { useState, useEffect, useRef } from "react";
import Select, { components, createFilter } from "react-select";
import { LocalizationProvider, DateCalendar } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { GlobalStyles } from "@mui/material";
import { fr } from "date-fns/locale";
import { useTranslation } from 'react-i18next';
import searchIcon from "../icons/search.svg";
import EyeIcon from "../icons/eyeg.svg";
import EyeOffIcon from "../icons/eye-offg.svg";
import calendarIcon from "../icons/calendar.svg";
import { formatAmountForm } from "../utils/format";

const Input = ({
  type = "text",
  name = "",
  label = "",
  options = [],
  value,
  onChange,
  disabled = false,
  isNormal = false,
  placeholder = "",
  required,
  context = "form",
  get = false,
  isDashboardFilter = false,
  isPasswordConfirmation = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const [localSelectedOptions, setLocalSelectedOptions] = useState(Array.isArray(value) ? [...value] : []);

  const isLabelActive = isFocused ||
    (type === "daterange" ? (startDate !== null) :
      (type === "checkbox-dropdown" ? localSelectedOptions.length > 0 :
        (Array.isArray(value) ? value.length > 0 : !!value)));

  const { t } = useTranslation();
  const datePickerRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (type === "checkbox-dropdown") {
      setLocalSelectedOptions(Array.isArray(value) ? [...value] : []);
    }
  }, [value, type]);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.match(/[a-z]/)) strength += 1;
    if (password.match(/[A-Z]/)) strength += 1;
    if (password.match(/[0-9]/)) strength += 1;
    if (password.match(/[^a-zA-Z0-9]/)) strength += 1;
    return strength;
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (type === "password" && !isPasswordConfirmation) {
      setPasswordStrength(checkPasswordStrength(newValue));
    }
  };

  const formatDateForPayload = (date, isEndDate = false) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    
    // For date ranges: start date = 00:00:00, end date = 23:59:59
    const hours = isEndDate ? "23" : "00";
    const minutes = isEndDate ? "59" : "00";
    const seconds = isEndDate ? "59" : "00";
    
    const timezoneOffset = -date.getTimezoneOffset();
    const absOffset = Math.abs(timezoneOffset);
    const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, "0");
    const offsetMinutes = String(absOffset % 60).padStart(2, "0");
    const timezone = `${timezoneOffset >= 0 ? "+" : "-"}${offsetHours}${offsetMinutes}`;

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezone}`;
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${day}-${month}-${year}`;
  };

  const formatDateForPayloadGet = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${day}/${month}/${year}`;
  };

  const formatDateBasedOnGetFlag = (date, isEndDate = false) => {
    if (!date) return "";
    return get ? formatDateForPayloadGet(date) : formatDateForPayload(date, isEndDate);
  };

  const handleDateSelection = (date) => {
    if (type === "daterange") {
      if (selectingStart) {
        setStartDate(date);
        setEndDate(null);
        setSelectingStart(false);
      } else if (date >= startDate) {
        setEndDate(date);
        setSelectingStart(true);
      } else {
        setStartDate(date);
        setEndDate(null);
      }
    } else if (type === "date") {
      setStartDate(date);
      setShowDatePicker(false);

      const formattedDate = {
        display: formatDateForDisplay(date),
        date: formatDateBasedOnGetFlag(date),
      };

      if (onChange) {
        onChange(formattedDate);
      }
    };
  };

  const resetSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectingStart(true);
    setShowDatePicker(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (value && typeof value === "object") {
      if ("startDate" in value || "endDate" in value) {
        setStartDate(value.startDate ? new Date(value.startDate) : null);
        setEndDate(value.endDate ? new Date(value.endDate) : null);
        setSelectingStart(!value.startDate || !!value.endDate);
        return;
      }

      if ("date" in value) {
        const parsedDate = new Date(value.date);
        if (!isNaN(parsedDate.getTime())) {
          setStartDate(parsedDate);
        }
        return;
      }
    } else {
      resetSelection();
    }

    if (typeof value === "string") {
      const parsedDate = new Date(value);
      if (!isNaN(parsedDate.getTime())) {
        setStartDate(parsedDate);
      }
    } else {
      resetSelection();
    }
  }, [value]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#ffffff",
      border: state.isFocused ? "1px solid #6A79F8" : "1px solid #E3E3E3",
      borderRadius: "8px",
      height: "48px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: 500,
      paddingLeft: "19px",
      "&:hover": {
        border: "1px solid #6A79F8",
      },

    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "8px 0",
    }),

    singleValue: (provided) => ({
      ...provided,
      color: "#000000",
      fontSize: "16px",
      fontWeight: 500,
      paddingTop: "8px",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.selectProps?.context === "filter" ? "#38485C" : "#AFB0B2",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#FFFFFF",
      borderRadius: "12px",
      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      marginTop: "4px",
      zIndex: 100,
      overflow: "hidden",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "0",
    }),

    option: (provided, state) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 20px",
      backgroundColor: state.isSelected
        ? "#6A79F8"
        : state.isFocused
          ? "#EDEFFF"
          : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#333",
      fontSize: "16px",
      fontWeight: state.isSelected ? "bold" : "semibold",
      cursor: "pointer",
      position: "relative",

      "&::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: "10%",
        width: "80%",
        height: "0.5px",
        backgroundColor: "#E4E7EA",
      },

      "&:last-child::after": {
        display: "none",
      },
    }),

    placeholder: (provided) => ({
      ...provided,
      fontSize: "16px",
      fontWeight: 500,
      color: "#000000",
      paddingTop: "8px",
    }),

  };

  const renderInputField = () => {
    switch (type) {
      case "checkbox-dropdown":
        const allOptionValue = "SELECT_ALL";

        const handleCheckboxChange = (optionValue, isChecked) => {
          let updatedValue = [...localSelectedOptions];

          if (optionValue === allOptionValue) {
            updatedValue = isChecked ? options.map((opt) => opt.value) : [];
          } else {
            if (isChecked) {
              updatedValue.push(optionValue);
            } else {
              updatedValue = updatedValue.filter((val) => val !== optionValue);
            }

            const allSelected = options.every((opt) => updatedValue.includes(opt.value));
            updatedValue = allSelected
              ? [allOptionValue, ...updatedValue]
              : updatedValue.filter((val) => val !== allOptionValue);
          }

          setLocalSelectedOptions(updatedValue);
        };

        const handleApply = () => {
          onChange(localSelectedOptions);
          setIsDropdownOpen(false);
        };

        const getDisplayText = () => {
          const selectedOptions = localSelectedOptions.filter((v) => v !== allOptionValue);
          if (localSelectedOptions.includes(allOptionValue)) {
            return "Tous";
          } else if (selectedOptions.length === 0) {
            return "";
          } else if (selectedOptions.length <= 2) {
            return selectedOptions
              .map((val) => options.find((opt) => opt.value === val)?.label || val)
              .join(", ");
          } else {
            return `${selectedOptions.length} ${t("placeholder.selected")}`;
          }
        };


        const MenuList = (props) => (
          <components.MenuList {...props} className="relative">
            <div className="overflow-y-auto max-h-[260px]">
              {props.children}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-[#E4E7EA]">
              <button
                onClick={handleApply}
                className="w-full pr-[21px] py-[10px] text-right text-[14px] font-bold text-[#6A78F8]"
              >
                {t("actions.apply")}
              </button>
            </div>
          </components.MenuList>
        );

        return (
          <Select
            options={[
              ...(isDashboardFilter
                ? [{
                  value: allOptionValue,
                  label: (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={localSelectedOptions.includes(allOptionValue)}
                        onChange={(e) => handleCheckboxChange(allOptionValue, e.target.checked)}
                        className="mr-2 cursor-pointer w-4 h-4 rounded border border-gray-300"
                        style={{ accentColor: "#6A78F8" }}
                      />
                      {localSelectedOptions.includes(allOptionValue)
                        ? t("placeholder.unselectAll")
                        : t("placeholder.selectAll")}
                    </div>
                  ),
                }]
                : []),
              ...options
                .filter((opt) => opt.value !== allOptionValue)
                .map((option) => ({
                  value: option.value,
                  label: (
                    <div
                      className="flex items-center cursor-pointer w-full"
                      onClick={() => handleCheckboxChange(option.value, !localSelectedOptions.includes(option.value))}
                    >
                      <input
                        type="checkbox"
                        checked={localSelectedOptions.includes(option.value)}
                        onChange={(e) => e.stopPropagation()}
                        className="mr-2 cursor-pointer w-4 h-4 rounded border border-gray-300"
                        style={{ accentColor: "#6A78F8" }}
                      />
                      {option.label || option.name}
                    </div>
                  ),
                })),
            ]}
            value={[]}
            placeholder={getDisplayText()}
            styles={{
              ...customSelectStyles,
              singleValue: (provided) => ({
                ...provided,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }),
            }}
            isDisabled={disabled}
            components={{ IndicatorSeparator: null, MenuList }}
            menuIsOpen={isDropdownOpen}
            onMenuOpen={() => setIsDropdownOpen(true)}
            onMenuClose={() => setIsDropdownOpen(false)}
            closeMenuOnSelect={false}
            context={context}
          />
        );

      case "dropdown":
        return (
          <Select
            options={options.map((option) => {
              if (typeof option === "string") {
                return { value: option, label: option };
              } else if (option.reference || option.name) {
                return {
                  value: option.id || option.value,
                  label: (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {option.reference && (
                        <span style={{ color: "#c5cbfa", fontWeight: "bold", fontSize: "14px" }}>
                          {option.reference}
                        </span>
                      )}
                      {option.name && (
                        <span style={{ color: "#fffff", fontSize: "12px" }}>
                          {option.name}
                        </span>
                      )}
                    </div>
                  ),
                  reference: option.reference,
                  name: option.name,
                };
              } else {
                return {
                  value: option.id || option.value,
                  label:
                    option.firstName && option.lastName
                      ? `${option.firstName} ${option.lastName}`
                      : option.label || option.name,
                };
              }
            })}
            value={(() => {
              const selectedOption = options.find(
                (option) => String(option.value) === String(value)
              );
              if (selectedOption?.reference && selectedOption?.name) {
                return { value, label: selectedOption.name };
              }
              return (
                options.find(
                  (option) =>
                    String(option.id || option.value) === String(value.id || value)
                ) || null
              );
            })()}
            onChange={(selectedOption) =>
              onChange && onChange(selectedOption ? selectedOption.value : null)
            }
            placeholder=""
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            styles={customSelectStyles}
            isDisabled={disabled}
            context={context}
            noOptionsMessage={() => t?.("placeholder.noResults")}

            isSearchable
            filterOption={createFilter({
              ignoreCase: true,
              trim: true,
              matchFrom: "any",
              stringify: (option) => {
                const d = option.data || {};
                if (d.reference || d.name) {
                  return `${d.reference ?? ""} ${d.name ?? ""}`.trim();
                }
                if (typeof d.label === "string") return d.label;
                if (d.firstName || d.lastName) {
                  return `${d.firstName ?? ""} ${d.lastName ?? ""}`.trim();
                }
                return String(d.value ?? "");
              },
            })}
          />
        );

      case "search":
        return (
          <div className="relative w-full h-[50px]">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A3A3A3]">
              <img src={searchIcon} alt="search" className="w-5 h-5" />
            </span>
            <input
              type="search"
              className="w-full h-[50px] pl-10  pr-[8px] text-[16px] font-medium placeholder-[#A3A3A3] text-black 
             border border-[#E3E3E3] rounded-lg 
             focus:border-primary hover:border-primary focus:outline-none"
              value={value}
              onChange={(e) => onChange && onChange(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={disabled}
              placeholder={t(`input.search`)}
            />
          </div>
        );

      case "toggle":
        const selectedOption = options.find((option) => option.value === value);
        const nextOption = options.find((option) => option.value !== value);

        return (
          <button
            onClick={() => nextOption && onChange(nextOption.value)}
            className={`flex items-center justify-center w-[50px] h-[50px] rounded-lg border border-[#E3E3E3] bg-white`}
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
            disabled={disabled}
            title={selectedOption?.tooltip || ''}
          >
            {selectedOption?.icon ? (
              <img
                src={selectedOption.icon}
                alt={selectedOption.tooltip || ''}
                width={24}
                height={24}
              />
            ) : (
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  backgroundColor: '#E3E3E3',
                }}
              />
            )}
          </button>
        );
      case "daterange":
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} locale={fr}>
            <GlobalStyles
              styles={{
                ".MuiPickersDay-root": {
                  fontFamily: "'Noto Sans', sans-serif !important",
                  fontWeight: "700 !important",
                  fontSize: "14px !important",
                  color: "#333 !important",
                  borderRadius: "12px !important",
                },
                ".MuiPickersDay-root.Mui-selected": {
                  backgroundColor: "#6A78F8 !important",
                  boxShadow: "0px 4px 10px rgba(106, 120, 248, 0.1) !important",
                  color: "#FFF !important",
                  borderRadius: "12px !important",
                },
                ".MuiPickersDay-root.in-range": {
                  backgroundColor: "#EDEFFF !important",
                  color: "#333 !important",
                  borderRadius: "0px !important",
                },
                ".MuiPickersDay-root.start-day": {
                  backgroundColor: "#6A78F8 !important",
                  color: "#FFF !important",
                  borderTopLeftRadius: "12px !important",
                  borderBottomLeftRadius: "12px !important",
                },
                ".MuiPickersDay-root.end-day": {
                  backgroundColor: "#6A78F8 !important",
                  color: "#FFF !important",
                  borderTopRightRadius: "12px !important",
                  borderBottomRightRadius: "12px !important",
                },
                ".MuiPickersDay-root:hover": {
                  backgroundColor: "#EDEFFF !important",
                },
              }}
            />
            <div className="relative w-full" ref={datePickerRef}>
              <div
                className="w-full h-12 px-4 flex items-center justify-between bg-white border border-[#E3E3E3] rounded-lg cursor-pointer hover:border-primary"
                onClick={() => setShowDatePicker(true)}
              >
                <span
                  className={`text-gray-700 text-[14px] font-semibold pl-[4px] ${label ? "pt-[12px]" : ""
                    }`}
                >
                  <span className="inline-flex flex-wrap">
                    {startDate && endDate
                      ? <>
                        <span>{formatDateForDisplay(startDate)}</span>
                        <span className="px-1">-</span>
                        <span>{formatDateForDisplay(endDate)}</span>
                      </>
                      : startDate
                        ? formatDateForDisplay(startDate)
                        : label
                          ? ""
                          : "Date"}
                  </span>
                </span>
                <img src={calendarIcon} alt="calendar" className="w-6 h-6" />
              </div>

              {showDatePicker && (
                <div className="absolute z-50 mt-2 bg-white shadow-lg rounded-lg p-4 flex justify-between min-h-[390px] ">
                  {/* Start Calendar */}
                  <div className="w-1/2 pr-2">
                    <div className="text-primary ml-6  -mb-3 text-sm font-semibold ">
                      {t('placeholder.startDate').toUpperCase()}
                    </div>
                    <DateCalendar
                      value={startDate}
                      onChange={handleDateSelection}
                      slotProps={{
                        day: (day, dayProps) => {
                          const date = day instanceof Date ? day : new Date(day || null);
                          if (isNaN(date.getTime())) return null;

                          const isStart = startDate && date.toDateString() === startDate.toDateString();
                          const isEnd = endDate && date.toDateString() === endDate.toDateString();
                          const isInRange = startDate && endDate && date > startDate && date < endDate;

                          let customClass = "";
                          if (isStart) customClass = "start-day";
                          else if (isEnd) customClass = "end-day";
                          else if (isInRange) customClass = "in-range";

                          return (
                            <div
                              {...dayProps}
                              className={`w-10 h-10 flex items-center justify-center cursor-pointer ${customClass}`}
                            >
                              {date.getDate()}
                            </div>
                          );
                        },
                      }}
                    />
                  </div>

                  {/* End Calendar */}
                  <div className="w-1/2 pl-2">
                    <div className="text-primary ml-6 -mb-3 text-[14px] font-semibold ">
                      {t('placeholder.endDate').toUpperCase()}
                    </div>
                    <DateCalendar
                      value={endDate}
                      onChange={handleDateSelection}
                      slotProps={{
                        day: (day, dayProps) => {
                          const date = day instanceof Date ? day : new Date(day || null);
                          if (isNaN(date.getTime())) return null;
                          const isStart = startDate && date.toDateString() === startDate.toDateString();
                          const isEnd = endDate && date.toDateString() === endDate.toDateString();
                          const isInRange = startDate && endDate && date > startDate && date < endDate;

                          let customClass = "";
                          if (isStart) customClass = "start-day";
                          else if (isEnd) customClass = "end-day";
                          else if (isInRange) customClass = "in-range";

                          return (
                            <div
                              {...dayProps}
                              className={`w-10 h-10 flex items-center justify-center cursor-pointer ${customClass}`}
                            >
                              {date.getDate()}
                            </div>
                          );
                        },
                      }}
                    />
                  </div>

                  {/* Buttons */}
                  <div className="absolute bottom-4 right-4 flex gap-3">
                    <button
                      className="px-4 py-2 rounded-[12px] text-primary hover:bg-[#EDEFFF] font-semibold text-[14px]"
                      onClick={resetSelection}
                    >
                      {t(`actions.cancel`)}
                    </button>
                    <button
                      className="px-4 py-2 rounded-[12px] bg-primary text-white hover:bg-[#5A69E0] font-semibold text-[14px]"
                      onClick={() => {
                        setShowDatePicker(false);
                        onChange({
                          startDate: startDate ? formatDateBasedOnGetFlag(startDate, false) : null,
                          endDate: endDate ? formatDateBasedOnGetFlag(endDate, true) : null,
                        });
                      }}
                    >
                      {t(`actions.apply`)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </LocalizationProvider>
        );

      case "date":
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} locale={fr}>
            <GlobalStyles
              styles={{
                ".MuiPickersDay-root": {
                  fontFamily: "'Noto Sans', sans-serif !important",
                  fontWeight: "700 !important",
                  fontSize: "14px !important",
                  color: "#333 !important",
                  borderRadius: "12px !important",
                },
                ".MuiPickersDay-root.Mui-selected": {
                  backgroundColor: "#6A78F8 !important",
                  boxShadow: "0px 4px 10px rgba(106, 120, 248, 0.1) !important",
                  color: "#FFF !important",
                  borderRadius: "12px !important",
                },
                ".MuiPickersDay-root:hover": {
                  backgroundColor: "#EDEFFF !important",
                },
              }}
            />
            <div className="relative w-full" ref={datePickerRef}>
              <div
                className={`w-full h-12 px-4 flex items-center justify-between bg-white border rounded-lg cursor-pointer ${context === "filter"
                  ? "border-[#E3E3E3] hover:border-primary"
                  : "border-gray-300 hover:border-primary"
                  }`}
                onClick={() => setShowDatePicker(true)}
              >
                <span
                  className={`text-[16px] pt-[12px] pl-[4px] font-medium mt-4${startDate
                    ? "text-gray-700"
                    : context === "filter"
                      ? "text-red-500"
                      : "text-[#AFB0B2]"
                    }`}
                >
                  {startDate ? formatDateForDisplay(startDate) : context === "form" ? placeholder : ""}
                </span>

                <img
                  src={calendarIcon}
                  alt="calendar"
                  className="w-6 h-6"
                  style={{
                    filter:
                      context === "filter"
                        ? "brightness(0) saturate(100%) invert(29%) sepia(11%) saturate(495%) hue-rotate(174deg) brightness(94%) contrast(85%)"
                        : "brightness(0) saturate(100%) invert(75%) sepia(4%) saturate(8%) hue-rotate(180deg)",
                  }}
                />
              </div>

              {showDatePicker && (
                <div className="absolute z-50 mt-2 bg-white shadow-lg rounded-lg p-4">
                  <DateCalendar
                    value={startDate}
                    onChange={(date) => {
                      handleDateSelection(date);
                    }}
                  />
                  {/* Buttons */}
                  <div className="absolute bottom-4 right-4 flex gap-3">
                    <button
                      className="px-4 py-2 rounded-[12px] text-primary hover:bg-[#EDEFFF] font-semibold text-[14px]"
                      onClick={resetSelection}
                    >
                      {t(`actions.cancel`)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </LocalizationProvider>
        );

      case "password":
        return (
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={value}
              onChange={(e) => {
                handleChange(e);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={`w-full h-[48px] px-[21px] border border-[#E3E3E3] rounded-lg pt-[22px] pb-[8px]
                  focus:border-primary focus:outline-none text-[16px] font-medium bg-white text-[#000000]`}
            />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-4 text-gray-500 text-sm"
            >
              <img
                src={showPassword ? EyeOffIcon : EyeIcon}
                alt="Toggle password visibility"
                className="w-5 h-5"
              />
            </button>

            {!isPasswordConfirmation && value.length > 0 && (
              <div className="mt-2 h-2 w-full bg-gray-200 rounded-lg relative">
                <div
                  className="absolute h-2 rounded-lg transition-all duration-300"
                  style={{
                    width: `${(passwordStrength / 5) * 100}%`,
                    backgroundColor:
                      passwordStrength <= 2 ? "red" :
                        passwordStrength === 3 ? "orange" :
                          passwordStrength >= 4 ? "green" : "transparent",
                  }}
                />
              </div>
            )}
          </div>
        );
      default:
        const isQuantity = name === "quantity";
        const isAmountField = name === "priceHT" || name === "priceTTC" || name === "amount";

        return (
          <input
            {...props}
            type={type}
            className={`w-full h-[48px] px-[21px] border border-[#E3E3E3] rounded-lg 
              focus:border-primary focus:outline-none hover:border-primary text-[16px] font-medium bg-white text-[#000000]
              ${isNormal ? "py-[12px]" : "pt-[22px] pb-[8px]"}
              ${props.className || ""}`}
            value={
              isAmountField && !isFocused
                ? formatAmountForm(value)
                : value
            }
            onChange={(e) => onChange && onChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              setIsFocused(false);
              if (isAmountField && onChange) {
                onChange(e.target.value);
              }
            }}
            min={isQuantity ? "1" : undefined}
            disabled={disabled}
            placeholder={placeholder}
          />
        );
    }
  };

  return (
    <div
      className={`relative ${type === "toggle" ? "w-auto" : "flex-shrink-0"}`}
      style={{
        flex: '1 1 calc(15% - 1rem)',
        minWidth: '150px',

      }}
    >
      {!isNormal && label && (
        <label
          className={`absolute px-[22px] transition-all duration-200 ${isLabelActive
            ? "top-[9px] text-[8px] font-semibold text-primary"
            : `top-[14.5px] text-[14px] font-semibold ${context === "filter" ? "text-[#38485C]" : "text-[#AFB0B2]"
            }`
            }`}
          style={{
            pointerEvents: "none",
            zIndex: 10,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "90%",
          }}
          title={label}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {renderInputField()}
    </div>
  );
};

export default Input;
